import { type SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import type { SystemAutoCompleteOption } from "../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import type { DepartmentLocation, LocationCredentialResponse } from "../../utils/models";
import { facilityToString, getUniqueLocations } from "../../utils/utilFunctions";

export type FacilityCredentialSortKey = keyof Pick<LocationCredentialResponse, "id" | "nurse_id" | "created_at" | "expires_at" | "status"> | "staff_name";

function findMatchingOption(
	options: SystemAutoCompleteOption<DepartmentLocation>[],
	facilityId: string | undefined,
	facilityName: string | undefined,
): SystemAutoCompleteOption<DepartmentLocation> | null {
	if (!facilityId && !facilityName) return null;
	// Match by ID first (most reliable)
	if (facilityId) {
		const byId = options.find((opt) => opt.value.id === facilityId);
		if (byId) return byId;
	}
	// Fall back to name match
	if (facilityName) {
		const decoded = decodeURIComponent(facilityName);
		return options.find((opt) => opt.value.location === decoded) ?? null;
	}
	return null;
}

export const useFacilityPageState = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const { facilityId, facilityName, departmentId } = useParams<{ facilityId?: string; facilityName?: string; departmentId?: string }>();
	const navigate = useNavigate();

	const autocompleteOptions = useMemo(() => {
		return getUniqueLocations(chosenDepartment.locations)
			.filter((location) => !!location.location)
			.sort((locA, locB) => locA.location.localeCompare(locB.location))
			.map<SystemAutoCompleteOption<DepartmentLocation>>((value) => ({
				value: value,
				label: facilityToString(value),
			}));
	}, [chosenDepartment.locations]);

	const [selectedLocation, setSelectedLocation] = useState<SystemAutoCompleteOption<DepartmentLocation> | null>(() => {
		const initialOptions = getUniqueLocations(chosenDepartment.locations)
			.filter((location) => !!location.location)
			.map<SystemAutoCompleteOption<DepartmentLocation>>((value) => ({ value, label: facilityToString(value) }));
		return findMatchingOption(initialOptions, facilityId, facilityName);
	});

	// Sync selected location when autocomplete options load (in case they weren't ready on init)
	useEffect(() => {
		if ((facilityId || facilityName) && !selectedLocation && autocompleteOptions.length > 0) {
			const match = findMatchingOption(autocompleteOptions, facilityId, facilityName);
			if (match) setSelectedLocation(match);
		}
	}, [facilityId, facilityName, autocompleteOptions, selectedLocation]);

	const [popoverOpen, setPopOverOpen] = useState(false);
	const [selectedCredentialForDelete, setSelectedCredentialForDelete] = useState<string | null>(null);
	const [sortKey, setSortKey] = useState<FacilityCredentialSortKey>("expires_at");
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

	const handleLocationChange = useCallback(
		(_: SyntheticEvent, value: SystemAutoCompleteOption<DepartmentLocation> | null) => {
			setSelectedLocation(value);
			if (value) {
				navigate(`/${departmentId}/facilities/${value.value.id}/${encodeURIComponent(value.value.location)}`);
			} else {
				navigate(`/${departmentId}/facilities`);
			}
		},
		[navigate, departmentId],
	);

	const handleTogglePopover = useCallback(() => {
		setPopOverOpen((prevState) => !prevState);
	}, []);

	const handleDeleteClick = useCallback((credentialId: string) => {
		setSelectedCredentialForDelete(credentialId);
	}, []);

	const handleDeleteCancel = useCallback(() => {
		setSelectedCredentialForDelete(null);
	}, []);

	const handleDeleteConfirm = useCallback(() => {
		setSelectedCredentialForDelete(null);
	}, []);

	const handleSort = useCallback((newSortKey: FacilityCredentialSortKey, direction: "asc" | "desc") => {
		setSortKey(newSortKey);
		setSortDirection(direction);
	}, []);

	const formattedNurseFullName = useCallback(
		(credential: LocationCredentialResponse): string => {
			const firstName = credential.nurse_first_name || "";
			const lastName = credential.nurse_last_name || "";
			const fullName = formatPersonName({ firstName, lastName });
			return fullName || t("credential.unknown.nurse", { defaultValue: "Unknown Nurse" });
		},
		[t, formatPersonName],
	);

	return {
		selectedLocation,
		handleLocationChange,
		autocompleteOptions,
		isPopoverOpen: popoverOpen,
		handleTogglePopover,
		selectedCredentialForDelete,
		showDeleteConfirmation: !!selectedCredentialForDelete,
		handleDeleteClick,
		handleDeleteCancel,
		handleDeleteConfirm,
		sortKey,
		sortDirection,
		handleSort,
		formattedNurseFullName,
	};
};
